// Liens d'un CV : portfolio, site, LinkedIn, et l'URL d'un projet.
//
// Le PDF n'est pas généré par nous : c'est Chrome qui imprime l'aperçu, et il
// garde un `<a href>` cliquable dans le fichier. Un lien dans le CV, c'est donc
// juste une ancre dans le HTML, et un `ExternalHyperlink` dans le Word. Deux
// choses restent à décider, et elles le sont ici, une seule fois :
//
// 1. QUELLE URL a le droit de devenir un lien. Elle vient d'une saisie, ou
//    d'une extraction IA d'un PDF. Seuls `http` et `https` passent : un
//    `javascript:` dans un aperçu, ou un `file:` dans un Word envoyé à un
//    recruteur, n'ont rien à faire là. Une adresse tapée sans schéma
//    (`monportfolio.be`) reçoit `https://`, c'est ce qu'on tape tous.
// 2. CE QU'ON AFFICHE. L'URL raccourcie, pas un libellé du type « Portfolio » :
//    un CV imprimé doit permettre de retaper l'adresse, et un ATS lit le texte
//    visible, pas la cible du lien.

/** Une adresse sans schéma qui ressemble à un nom de domaine (`a.be/x`). */
const DOMAINE_NU = /^[\p{L}\p{N}][\p{L}\p{N}-]*(\.[\p{L}\p{N}-]+)+(:\d+)?([/?#]\S*)?$/u

/**
 * L'URL normalisée et sûre à mettre dans un `href`, ou `null` si la saisie ne
 * doit pas devenir un lien (vide, schéma refusé, pas une adresse).
 */
export function safeHref(raw: string | undefined | null): string | null {
  const s = (raw ?? '').trim()
  if (!s || /\s/.test(s)) return null
  const avecSchema = /^[a-z][a-z0-9+.-]*:/i.test(s) ? s : DOMAINE_NU.test(s) ? `https://${s}` : null
  if (!avecSchema) return null
  try {
    const u = new URL(avecSchema)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null
    if (!u.hostname.includes('.')) return null
    return u.href
  } catch {
    return null
  }
}

/** La forme lisible d'une URL : sans schéma, sans `www.`, sans `/` final. */
export function displayUrl(href: string): string {
  return href
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '')
}

export interface ResumeLink {
  /** Pour l'attribut `href` et le Word. */
  href: string
  /** Pour le texte visible. */
  text: string
}

/** Un lien prêt à rendre, ou `null` pour une saisie qui n'en est pas un. */
export function toResumeLink(raw: string | undefined | null): ResumeLink | null {
  const href = safeHref(raw)
  return href ? { href, text: displayUrl(href) } : null
}
